import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import InteractivePhoneSection from '../components/homepage/InteractivePhoneSection';

const HomePage = () => {
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    document.body.style.fontFamily = '"Inter", sans-serif';
  }, []);

  const { scrollY } = useScroll();
  const scrollIndicatorOpacity = useTransform(scrollY, [0, 3000, 3500], [1, 1, 0]);

  return (
    <div className="min-h-screen font-sans text-gray-900 bg-white">
      <main>
        {/* 1. Hero Section */}
        <section className="relative min-h-[80vh] md:min-h-[90vh] flex items-center justify-center overflow-hidden bg-gray-900 text-white">
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-blue-900 to-gray-900 opacity-90"></div>
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1504328345606-18bbc8c9d7d1?ixlib=rb-1.2.1&auto=format&fit=crop&w=1950&q=80')] bg-cover bg-center mix-blend-overlay opacity-30"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10 text-center pt-20 pb-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-6 leading-tight">
                Run Your Buisness <br />
                <span className="text-blue-400">From Your Truck</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto font-light">
                You provide great services and SwiftBidPro built the tools to help you manage them. Professional quoting and invoicing for the modern contractor.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Link to="/register">
                  <button className="w-full sm:w-auto px-8 py-4 bg-blue-600 text-white rounded-xl font-bold text-lg hover:bg-blue-500 transition-all shadow-lg hover:shadow-blue-500/50 transform hover:-translate-y-1">
                    Sign Up for Free
                  </button>
                </Link>
                <Link to="/login">
                  <button className="w-full sm:w-auto px-8 py-4 bg-transparent border border-gray-500 text-white rounded-xl font-bold text-lg hover:bg-white/10 transition-all">
                    Login
                  </button>
                </Link>
              </div>
            </motion.div>
          </div>

          {/* Scroll Indicator - Sticky for Hero and Phone Section */}
          <motion.div
            style={{ opacity: scrollIndicatorOpacity }}
            className="hidden md:flex fixed bottom-10 left-1/2 -translate-x-1/2 text-gray-400 flex-col items-center gap-2 z-50 pointer-events-none"
          >
            <span className="text-xs uppercase tracking-widest text-shadow-sm">Scroll to explore</span>
            <div className="w-6 h-10 border-2 border-gray-400 rounded-full flex justify-center p-1 bg-black/20 backdrop-blur-sm">
              <motion.div
                animate={{ y: [0, 12, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
                className="w-1 h-2 bg-white rounded-full shadow-sm"
              />
            </div>
          </motion.div>
        </section>

        {/* 2. Interactive Phone Section */}
        <InteractivePhoneSection />

        {/* 3. The "Digital Filing Cabinet" Section */}
        <section className="py-24 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center gap-16">
              <div className="hidden md:block w-full md:w-1/2">
                <div className="bg-white p-8 rounded-3xl shadow-xl border border-gray-100 relative overflow-hidden">
                  <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600">
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h10a2 2 0 012 2v14a2 2 0 01-2 2zM7 9h10M7 13h10"></path></svg>
                        </div>
                        <div className="flex-1">
                          <div className="h-4 bg-gray-200 rounded w-1/3 mb-2"></div>
                          <div className="h-3 bg-gray-100 rounded w-1/2"></div>
                        </div>
                        <div className="text-green-600 text-sm font-bold">Paid</div>
                      </div>
                    ))}
                  </div>
                  <div className="absolute bottom-0 left-0 w-full h-24 bg-gradient-to-t from-white to-transparent"></div>
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                  All your business data, right where you need it.
                </h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Every quote, every invoice, every client interaction. Searchable, organized, and safe.
                  Whether it's tax time or a repeat customer calling 2 years later, you have the answer in seconds.
                </p>
                <ul className="space-y-4">
                  {[
                    "Client History & Notes",
                    "Past Quotes & Invoices",
                    "Revenue Tracking",
                    "Secure Cloud Storage"
                  ].map((item, i) => (
                    <li key={i} className="flex items-center text-gray-700 font-medium">
                      <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 text-blue-600">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                      </div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 4. The "One-Man Army" Grid */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">Built for those Who Do It All</h2>
              <p className="text-xl text-gray-600">You wear all the hats. We make them fit better.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  title: "Templates Save Your Sanity",
                  desc: "Don't type 'prep and paint' for the 100th time. Save it once, use it forever. Build quotes like lego blocks.",
                  icon: "🧩"
                },
                {
                  title: "Know Your Numbers",
                  desc: "Dashboard shows exactly who owes you money. No more digging through texts or wondering if you got paid.",
                  icon: "📈"
                },
                {
                  title: "Win More Jobs",
                  desc: "Clients hire the first person to get them a professional price. Be that person. Impress them before you even start.",
                  icon: "🏆"
                }
              ].map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.1 }}
                  viewport={{ once: true }}
                  className="p-8 rounded-2xl bg-gray-50 border border-gray-100 hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="text-5xl mb-6 transform group-hover:scale-110 transition-transform duration-300">{item.icon}</div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-lg">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* 5. Book Jobs From The Ladder */}
        <section className="py-24 bg-gray-900 text-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center gap-16">
              <div className="w-full md:w-1/2 order-2 md:order-1">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">Book Jobs From The Ladder</h2>
                <p className="text-lg text-gray-400 mb-8 leading-relaxed">
                  You can't always answer the phone when you're working, but you can't afford to miss leads.
                  Send a booking link when you can't talk, and let clients schedule themselves.
                </p>
                <div className="flex items-center gap-4 text-blue-400 font-bold">
                  <div className="w-10 h-10 rounded-full bg-blue-900/50 flex items-center justify-center border border-blue-500">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
                  </div>
                  Missed calls are missed money.
                </div>
              </div>
              <div className="w-full md:w-1/2 order-1 md:order-2 flex justify-center">
                {/* Visual: Phone Notification */}
                <div className="w-72 bg-gray-800 rounded-3xl p-4 border-4 border-gray-700 shadow-2xl transform rotate-3 hover:rotate-0 transition-transform duration-500">
                  <div className="bg-gray-900 rounded-xl p-4 mb-3 border border-gray-700">
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white text-xs">S</div>
                        <span className="font-bold text-sm">SwiftBidPro</span>
                      </div>
                      <span className="text-xs text-gray-500">Now</span>
                    </div>
                    <p className="text-sm text-gray-300">New Appointment Booked!</p>
                    <p className="text-xs text-gray-500">John Doe scheduled an estimate for Tuesday at 2 PM.</p>
                  </div>
                  <div className="bg-blue-600 rounded-lg p-3 text-center text-sm font-bold cursor-pointer hover:bg-blue-500">
                    View Calendar
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* 6. Sell On The Spot */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center gap-16">
              <div className="w-full md:w-1/2">
                {/* Visual: Signature Pad */}
                <div className="bg-gray-50 p-8 rounded-3xl shadow-lg border border-gray-100 transform -rotate-2 hover:rotate-0 transition-transform duration-500">
                  <div className="bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                    <div className="flex justify-between items-center mb-6 border-b border-gray-100 pb-4">
                      <span className="font-bold text-gray-900">Total Estimate</span>
                      <span className="font-bold text-2xl text-blue-600">$4,250.00</span>
                    </div>
                    <div className="space-y-4 mb-6">
                      <div className="h-32 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center relative">
                        <span className="text-gray-400 text-sm">Client Signature</span>
                        <svg className="absolute w-32 h-16 text-blue-600 opacity-80" viewBox="0 0 100 50">
                          <path d="M10,40 Q30,10 50,30 T90,20" fill="none" stroke="currentColor" strokeWidth="3" />
                        </svg>
                      </div>
                    </div>
                    <button className="w-full py-3 bg-green-600 text-white rounded-lg font-bold shadow-md">
                      Accept & Schedule
                    </button>
                  </div>
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Sell On The Spot</h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Don't go home to quote. Build the price in the driveway, get the signature, and schedule the work before you leave.
                  Stop playing phone tag and start closing deals face-to-face.
                </p>
                <ul className="space-y-3">
                  {["Create quote in minutes", "Digital signature capture", "Collect deposit instantly"].map((item, i) => (
                    <li key={i} className="flex items-center text-gray-700 font-medium">
                      <svg className="w-5 h-5 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* 7. We Build Your Templates (White Glove) */}
        <section className="py-24 bg-blue-50">
          <div className="container mx-auto px-4 text-center">
            <div className="max-w-3xl mx-auto">
              <div className="inline-block bg-blue-100 text-blue-700 px-4 py-1 rounded-full text-sm font-bold mb-6">
                CONCIERGE ONBOARDING
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Hate Setup? We'll Do It For You.</h2>
              <p className="text-xl text-gray-600 mb-10 leading-relaxed">
                Setting up software is a pain. Send us your price list, your old spreadsheets, or even a picture of your napkin notes.
                We'll build your custom templates and item lists for you, for free.
              </p>
              <div className="grid md:grid-cols-3 gap-6 text-left">
                {[
                  { title: "Send Your Data", desc: "Email us your current pricing.", icon: "📤" },
                  { title: "We Build It", desc: "Our team digitizes everything.", icon: "⚙️" },
                  { title: "Ready to Roll", desc: "Login to a fully stocked account.", icon: "🚀" }
                ].map((step, idx) => (
                  <div key={idx} className="bg-white p-6 rounded-xl shadow-sm border border-blue-100">
                    <div className="text-3xl mb-3">{step.icon}</div>
                    <h3 className="font-bold text-gray-900 mb-1">{step.title}</h3>
                    <p className="text-sm text-gray-500">{step.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* 8. Founding Members Section */}
        <section className="py-24 bg-blue-900 text-white relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-full overflow-hidden">
            <div className="absolute top-[-50%] left-[-20%] w-[800px] h-[800px] bg-blue-800 rounded-full blur-3xl opacity-30"></div>
            <div className="absolute bottom-[-50%] right-[-20%] w-[800px] h-[800px] bg-blue-600 rounded-full blur-3xl opacity-30"></div>
          </div>

          <div className="container mx-auto px-4 relative z-10 text-center">
            <div className="inline-block bg-blue-800/50 backdrop-blur-sm border border-blue-700 px-4 py-1 rounded-full text-blue-200 text-sm font-semibold mb-6">
              OPEN BETA ACCESS
            </div>
            <h2 className="text-4xl md:text-6xl font-bold mb-6">
              Join the Founding Members Program
            </h2>
            <p className="text-xl md:text-2xl text-blue-200 mb-10 max-w-3xl mx-auto font-light leading-relaxed">
              We're building the ultimate tool for owner-operators, and we want your input.
              Use SwiftBidPro for <span className="text-white font-bold">free</span> while we refine the system.
              No credit card, no hidden fees—just great software that gets better every week.
            </p>

            <div className="flex flex-col items-center gap-6">
              <Link to="/register">
                <button className="bg-white text-blue-900 px-12 py-5 rounded-xl font-bold text-xl hover:bg-blue-50 transition-all shadow-2xl transform hover:-translate-y-1">
                  Claim Your Free Account
                </button>
              </Link>
              <p className="text-sm text-blue-300">
                Limited spots available for the beta program.
              </p>
            </div>
          </div>
        </section>

        <footer className="bg-gray-900 text-white py-12 border-t border-gray-800">
          <div className="container mx-auto px-4">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              <div className="col-span-1 md:col-span-2">
                <span className="text-2xl font-bold block mb-4">SwiftBidPro</span>
                <p className="text-gray-400 max-w-xs">
                  Built for the trade.
                </p>
              </div>
              <div>
                <h4 className="font-bold mb-4 text-gray-300">Product</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><Link to="/features" className="hover:text-white transition-colors">Features</Link></li>
                  <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                  <li><Link to="/why-i-built-this" className="hover:text-white transition-colors">Why I Built This</Link></li>
                  <li><Link to="/login" className="hover:text-white transition-colors">Login</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="font-bold mb-4 text-gray-300">Legal</h4>
                <ul className="space-y-2 text-gray-400">
                  <li><Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link></li>
                  <li><Link to="/terms-of-service" className="hover:text-white transition-colors">Terms of Service</Link></li>
                </ul>
              </div>
            </div>
            <div className="pt-8 border-t border-gray-800 text-center text-sm text-gray-500">
              © {new Date().getFullYear()} SwiftBidPro. All rights reserved.
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default HomePage;
